// Map a Sefaria source CATEGORY to a "source type" descriptor: a stable bucket
// key, a CSS color custom-property name, a decorative Hebrew glyph, and an honest
// He/En type label. Color is NEVER the sole signal on a source card - it is always
// paired with the text label - so the label/labelHe fields are the load-bearing
// part of this descriptor, and the colorVar/glyph are supporting decoration.

export interface SourceType {
  key: string; // stable bucket key: "commentary"|"targum"|"talmud"|"midrash"|"halakha"|"thought"|"tanakh"|"other"
  colorVar: string; // a CSS custom property name, e.g. "--t-commentary"
  glyph: string; // a single Hebrew letter glyph (decorative; paired with label)
  label: string; // English type label
  labelHe: string; // Hebrew type label
}

const COMMENTARY: SourceType = {
  key: "commentary",
  colorVar: "--t-commentary",
  glyph: "פ",
  label: "Commentary",
  labelHe: "פירוש",
};
const TARGUM: SourceType = {
  key: "targum",
  colorVar: "--t-targum",
  glyph: "ת",
  label: "Targum",
  labelHe: "תרגום",
};
const TALMUD: SourceType = {
  key: "talmud",
  colorVar: "--t-talmud",
  glyph: "ג",
  label: "Talmud",
  labelHe: "גמרא",
};
const MIDRASH: SourceType = {
  key: "midrash",
  colorVar: "--t-midrash",
  glyph: "מ",
  label: "Midrash",
  labelHe: "מדרש",
};
const HALAKHA: SourceType = {
  key: "halakha",
  colorVar: "--t-halakha",
  glyph: "ה",
  label: "Halacha",
  labelHe: "הלכה",
};
const THOUGHT: SourceType = {
  key: "thought",
  colorVar: "--t-thought",
  glyph: "ח",
  label: "Jewish Thought",
  labelHe: "מחשבה",
};
const TANAKH: SourceType = {
  key: "tanakh",
  colorVar: "--t-tanakh",
  glyph: "נ",
  label: "Tanakh",
  labelHe: "תנ״ך",
};
// Unknown / unrecognized category. Reuses the commentary color var (the neutral
// default accent) but carries its own honest "Source" / "מקור" label and glyph so
// the card never lies about what it is.
const OTHER: SourceType = {
  key: "other",
  colorVar: "--t-commentary",
  glyph: "ס",
  label: "Source",
  labelHe: "מקור",
};

// Sefaria top-level category (lowercased) -> descriptor. Several categories fold
// into one bucket (Mishnah/Talmud/Tosefta -> talmud; the machshava family ->
// thought; Responsa -> halakha).
const BY_CATEGORY: Record<string, SourceType> = {
  commentary: COMMENTARY,
  targum: TARGUM,
  mishnah: TALMUD,
  talmud: TALMUD,
  tosefta: TALMUD,
  midrash: MIDRASH,
  halakhah: HALAKHA,
  responsa: HALAKHA,
  kabbalah: THOUGHT,
  chasidut: THOUGHT,
  musar: THOUGHT,
  "jewish thought": THOUGHT,
  philosophy: THOUGHT,
  tanakh: TANAKH,
};

// Resolve a category to its source-type descriptor. Case-insensitive and
// whitespace-trimmed; any unrecognized category falls back to the OTHER bucket.
export function getSourceType(category: string): SourceType {
  const key = category.trim().toLowerCase();
  return BY_CATEGORY[key] ?? OTHER;
}

// All eight descriptors in canonical display order. Feeds the help-modal color
// legend and the Explore stats box, so both surfaces speak the exact same
// vocabulary as the per-card and per-verse dots.
export const ALL_SOURCE_TYPES: readonly SourceType[] = [
  COMMENTARY,
  TARGUM,
  TALMUD,
  MIDRASH,
  HALAKHA,
  THOUGHT,
  TANAKH,
  OTHER,
];

// ---------------------------------------------------------------------------
// Tanakh sections (Chumash / Navi / Ketuvim)
//
// Static canonical classification, same posture as the taxonomy allowlists:
// it only ever REFINES a real "Tanakh" category coming off a real Sefaria
// response - it never invents one. The titles below are Sefaria's canonical
// English work titles (e.g. "I Samuel", "Song of Songs"). A book we do not
// recognize stays in the plain Tanakh bucket (null), so drift in Sefaria's
// canon can never mislabel a source.

export interface TanakhSection {
  key: "chumash" | "navi" | "ketuvim";
  label: string; // English section label
  labelHe: string; // Hebrew section label
}

const CHUMASH: TanakhSection = { key: "chumash", label: "Chumash", labelHe: "חומש" };
const NAVI: TanakhSection = { key: "navi", label: "Navi", labelHe: "נביאים" };
const KETUVIM: TanakhSection = { key: "ketuvim", label: "Ketuvim", labelHe: "כתובים" };

const CHUMASH_BOOKS = new Set([
  "Genesis",
  "Exodus",
  "Leviticus",
  "Numbers",
  "Deuteronomy",
]);

const NAVI_BOOKS = new Set([
  "Joshua",
  "Judges",
  "I Samuel",
  "II Samuel",
  "I Kings",
  "II Kings",
  "Isaiah",
  "Jeremiah",
  "Ezekiel",
  "Hosea",
  "Joel",
  "Amos",
  "Obadiah",
  "Jonah",
  "Micah",
  "Nahum",
  "Habakkuk",
  "Zephaniah",
  "Haggai",
  "Zechariah",
  "Malachi",
]);

const KETUVIM_BOOKS = new Set([
  "Psalms",
  "Proverbs",
  "Job",
  "Song of Songs",
  "Ruth",
  "Lamentations",
  "Ecclesiastes",
  "Esther",
  "Daniel",
  "Ezra",
  "Nehemiah",
  "I Chronicles",
  "II Chronicles",
]);

// Classify a canonical Tanakh book title into its section, or null when the
// title is not a recognized Tanakh book.
export function tanakhSection(bookTitle: string): TanakhSection | null {
  const t = bookTitle.trim();
  if (CHUMASH_BOOKS.has(t)) return CHUMASH;
  if (NAVI_BOOKS.has(t)) return NAVI;
  if (KETUVIM_BOOKS.has(t)) return KETUVIM;
  return null;
}
